import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  TextField,
  Collapse,
  Paper,
  FormControl,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SubdirectoryArrowRight as SubIcon,
} from '@mui/icons-material';
import { Week, Topic, Day, Subtopic  } from '../../../types/curriculum.types.ts';
import DayItem from './Dayitem.tsx';
import InlineEdit from './Inlineedit.tsx';

interface WeekItemProps {
  week: Week;
  weekIndex: number;
  availableTopics: Topic[];
  onUpdate: (id: string, patch: Partial<Week>) => void;
  onDelete: (id: string) => void;
  onUpdateTopic: (weekId: string, topicId: string) => void;
  onAddSubtopic: (weekId: string) => void;
  onUpdateSubtopic: (weekId: string, subtopicId: string, patch: Partial<Subtopic>) => void;
  onDeleteSubtopic: (weekId: string, subtopicId: string) => void;
  onAddDay: (weekId: string) => void;
  onUpdateDay: (weekId: string, dayId: string, patch: Partial<Day>) => void;
  onDeleteDay: (weekId: string, dayId: string) => void;
}

export default function WeekItem({
  week,
  weekIndex,
  availableTopics,
  onUpdate,
  onDelete,
  onUpdateTopic,
  onAddSubtopic,
  onUpdateSubtopic,
  onDeleteSubtopic,
  onAddDay,
  onUpdateDay,
  onDeleteDay,
}: WeekItemProps) {
  const [expanded, setExpanded] = useState(true);
  const [viewMode, setViewMode] = useState<'subtopics' | 'days'>('days');

  const totalMarks = week.subtopics.reduce((sum, st) => sum + (st.marks || 0), 0);
  const totalObjectives = week.days.reduce((sum, d) => sum + d.objectives.length, 0);
  const holidayCount = week.days.filter(d => d.isHoliday).length;

  const weekdays = week.days.filter(d => !['Saturday', 'Sunday'].includes(d.dayOfWeek));
  const weekendDays = week.days.filter(d => ['Saturday', 'Sunday'].includes(d.dayOfWeek));

  const currentTopic = availableTopics.find(t => t.id === week.topicId);
  const currentSubtopics = currentTopic?.subtopics || [];

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        border: '1px solid #E2E8F0',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#FAFBFC',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', flexWrap: 'wrap' }}>
        <Chip label={`W${weekIndex + 1}`} size="medium" sx={{ fontSize: '0.7rem', height: 26, bgcolor: '#8B5CF6', color: 'white', fontWeight: 700 }} />

        {/* Topic Dropdown */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select value={week.topicId || ''} onChange={(e) => onUpdateTopic(week.id, e.target.value)} displayEmpty sx={{ fontSize: '0.75rem', height: 32 }}>
            <MenuItem value="" sx={{ fontSize: '0.75rem' }}><em>Select Topic</em></MenuItem>
            {availableTopics.map((topic) => (
              <MenuItem key={topic.id} value={topic.id} sx={{ fontSize: '0.75rem' }}>
                {topic.name} {topic.duration ? `(${topic.duration}w)` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <InlineEdit value={week.name} onSave={(v) => onUpdate(week.id, { name: v })} variant="body1" bold placeholder="Week name" fontSize="0.95rem" />

        <Chip label={`${week.days.length} days`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#E0E7FF', color: '#3730A3' }} />
        {holidayCount > 0 && (
          <Chip label={`${holidayCount} holidays`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#FEE2E2', color: '#991B1B' }} />
        )}
        <Chip label={`${totalObjectives} objectives`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#D1FAE5', color: '#065F46' }} />
        <Chip label={`${totalMarks} marks`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E' }} />

        <ToggleButtonGroup
          value={viewMode} exclusive size="small"
          onChange={(_e, val) => val && setViewMode(val)}
          sx={{ '& .MuiToggleButton-root': { fontSize: '0.65rem', py: 0.5, px: 1.5, minHeight: 28 } }}
        >
          <ToggleButton value="subtopics">Subtopics</ToggleButton>
          <ToggleButton value="days">Daily Plan</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          <Tooltip title={expanded ? 'Collapse' : 'Expand'} arrow>
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.5 }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 22 }} /> : <ExpandMoreIcon sx={{ fontSize: 22 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Week" arrow>
            <IconButton size="small" color="error" onClick={() => onDelete(week.id)} sx={{ p: 0.5 }}>
              <DeleteIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Topic Info */}
      {week.topicId && currentTopic && (
        <Box sx={{ px: 2.5, py: 0.5, bgcolor: '#F0FDF4', borderBottom: '1px solid #E2E8F0' }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#065F46' }}>
            📚 Topic: {currentTopic.name} {currentTopic.description ? `- ${currentTopic.description}` : ''}
            {currentSubtopics.length > 0 && ` (${currentSubtopics.length} subtopics)`}
          </Typography>
        </Box>
      )}

      <Collapse in={expanded}>
        <Box sx={{ p: 2.5 }}>

          {/* ── Subtopics View ── */}
          {viewMode === 'subtopics' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.9rem' }}>Subtopics</Typography>
                <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={() => onAddSubtopic(week.id)}
                  sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5, minHeight: 32 }}
                >Add Subtopic</Button>
              </Box>

              {week.subtopics.length === 0 && (
                <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.85rem' }}>
                  No subtopics. {week.topicId ? 'Add subtopics or select a different topic.' : 'Select a topic first.'}
                </Typography>
              )}

              {week.subtopics.map((subtopic, si) => (
                <Paper key={subtopic.id} elevation={0}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, mb: 0.5, borderRadius: 1.5, bgcolor: '#FAFAFA', border: '1px solid #E2E8F0', flexWrap: 'wrap' }}
                >
                  <SubIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                  <Chip label={`${si + 1}`} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#94A3B8', color: 'white', minWidth: 22 }} />
                  <InlineEdit value={subtopic.name} onSave={(v) => onUpdateSubtopic(week.id, subtopic.id, { name: v })} variant="body2" placeholder="Subtopic name" fontSize="0.85rem" />
                  <TextField size="small" type="number" value={subtopic.marks || 0}
                    onChange={(e) => onUpdateSubtopic(week.id, subtopic.id, { marks: Math.max(0, Number(e.target.value)) })}
                    inputProps={{ min: 0, style: { width: 50, padding: '4px 6px', fontSize: '0.8rem' } }}
                    sx={{ width: 55 }} variant="outlined" />
                  <IconButton size="small" color="error" onClick={() => onDeleteSubtopic(week.id, subtopic.id)} sx={{ p: 0.5 }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Paper>
              ))}
            </>
          )}

          {/* ── Daily Plan View ── */}
          {viewMode === 'days' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.9rem' }}>
                  Daily Schedule ({weekdays.length} weekdays + {weekendDays.length} weekend days)
                  {holidayCount > 0 && ` - ${holidayCount} holidays`}
                </Typography>
                <Button size="small" variant="contained" startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={() => onAddDay(week.id)}
                  sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5, minHeight: 32 }}
                >Add Day</Button>
              </Box>

              {week.days.length === 0 && (
                <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.85rem' }}>No days planned</Typography>
              )}

              {weekdays.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#3B82F6', fontSize: '0.85rem', display: 'block', mb: 1 }}>Weekdays</Typography>
                  {weekdays.map((day, di) => (
                    <DayItem key={day.id} day={day} dayIndex={di}
                      availableSubtopics={currentSubtopics}
                      onUpdateDay={(id, patch) => onUpdateDay(week.id, id, patch)}
                      onDeleteDay={(id) => onDeleteDay(week.id, id)}
                    />
                  ))}
                </>
              )}

              {weekendDays.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#F59E0B', fontSize: '0.85rem', display: 'block', mt: 1.5, mb: 1 }}>Weekend</Typography>
                  {weekendDays.map((day, di) => (
                    <DayItem key={day.id} day={day} dayIndex={di}
                      availableSubtopics={currentSubtopics}
                      onUpdateDay={(id, patch) => onUpdateDay(week.id, id, patch)}
                      onDeleteDay={(id) => onDeleteDay(week.id, id)}
                    />
                  ))}
                </>
              )}

              {!week.topicId && (
                <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.75rem', py: 0.5 }}>
                  Please select a topic for this week to assign subtopics to days.
                </Alert>
              )}
              {week.topicId && currentSubtopics.length === 0 && (
                <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.75rem', py: 0.5 }}>
                  No subtopics available for this topic. Add subtopics in the "Subtopics" view.
                </Alert>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}